import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '../../lib/utils';

// ================================================================= //
//          Button Variants using CVA (Class Variance Authority)     //
// ================================================================= //
// This defines all the different styles our button can have.
// It's a clean way to manage complex component styling with Tailwind CSS.

const buttonVariants = cva(
  // Base styles applied to all variants
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      // --- Style Variants ---
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-sm',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      // --- Size Variants ---
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    // Default styles if not specified
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

// ================================================================= //
//          TypeScript Props Interface                             //
// ================================================================= //
// Extends standard HTML button attributes with our custom props.

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  Icon?: React.ElementType;
  iconPosition?: 'left' | 'right';
  /**
   * When `asChild` is true the button will *not* render a native
   * `<button>` element. Instead it will clone its single child and
   * merge the button styles onto that element.  
   *
   * This pattern mirrors Radix UI’s `asChild` prop and lets us use
   * `Button` with React-Router’s `<Link>` or any other component that
   * ultimately renders a valid interactive element.
   */
  asChild?: boolean;
}

// ================================================================= //
//          Button Component Implementation                        //
// ================================================================= //
// Uses React.forwardRef to allow parent components to pass a ref.

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      children,
      isLoading,
      Icon,
      iconPosition = 'left',
      asChild = false,
      ...props
    },
    ref
  ) => {
    // Determine the content to display inside the button
    const buttonContent = (
      <>
        {/* Render Icon on the left if specified */}
        {Icon && iconPosition === 'left' && (
          <Icon className="mr-2 h-4 w-4" />
        )}
        
        {children}
        
        {/* Render Icon on the right if specified */}
        {Icon && iconPosition === 'right' && (
          <Icon className="ml-2 h-4 w-4" />
        )}
      </>
    );

    return (
      asChild && React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement, {
            className: cn(
              buttonVariants({ variant, size, className }),
              (children as React.ReactElement).props.className
            ),
            ref,
            disabled: isLoading,
            'aria-disabled': isLoading,
            ...props,
          })
        : (
          <button
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            disabled={isLoading}
            aria-disabled={isLoading}
            {...props}
          >
            {/* Show loader when in loading state, otherwise show content */}
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              buttonContent
            )}
          </button>
        )
    );
  }
);

// Set a display name for easier debugging in React DevTools
Button.displayName = 'Button';

export { Button, buttonVariants };
